import { supabase } from "@/services/supabase/client";
import { FunctionsFetchError, FunctionsHttpError, FunctionsRelayError } from "@supabase/supabase-js";
import { normalizeTagColor } from "@/features/tags/utils";
import { normalizeFolderColor } from "@/features/folders/utils/general";
import { TagColor } from "@/features/tags/types";
import { FolderColor } from "@/features/folders/types";

// Custom error class to preserve original error context
export class EdgeFunctionError extends Error {
  constructor(
    message: string,
    public originalError: unknown
  ) {
    super(message);
    this.name = "PromptError";
  }
}

async function handleEdgeFunctionError(error: unknown): Promise<void> {
  if (error instanceof FunctionsHttpError) {
    const res = await error.context.json();
    throw new EdgeFunctionError(res.error, error);
  } else if (error instanceof FunctionsRelayError) {
    throw new EdgeFunctionError(`Relay error: ${error.message}`, error);
  } else if (error instanceof FunctionsFetchError) {
    throw new EdgeFunctionError(`Fetch error: ${error.message}`, error);
  } else if (error) {
    throw new EdgeFunctionError("Unknown error", error);
  }
}

interface GenerateStudySubjectParams {
  input: string;
  length: "short" | "medium" | "long";
}

interface SummarizeNoteParams {
  content: string;
}

interface ExtractTodosParams {
  content: string;
}

interface GenerateTitleParams {
  content: string;
}

interface FixGrammarAndSpellingParams {
  content: string;
}

interface ImproveWritingParams {
  content: string;
}

interface ImproveFormattingParams {
  content: string;
}

interface ChangeToneParams {
  content: string;
  tone: "Professional" | "Casual" | "Straightforward" | "Confident" | "Friendly";
}

interface ChangeLengthParams {
  content: string;
  action: "Make longer" | "Make shorter";
}

interface TranslateNoteParams {
  content: string;
  language: string;
}

interface AskAIParams {
  question: string;
  content?: string;
}

interface SuggestTagsParams {
  content: string;
  existingTags?: Array<{ name: string; color: string }>;
}

export interface SuggestStructureParams {
  useCase: string;
  structureType: "folders" | "tags" | "folders-and-tags";
}

interface SpeechToTextParams {
  transcribedText: string;
  language: string;
}

interface SuggestedTag {
  name: string;
  color: TagColor;
}

export interface SuggestedFolder {
  name: string;
  color: FolderColor;
  children: SuggestedFolder[];
}

export interface SuggestedStructureTag {
  name: string;
  color: TagColor;
}

export interface SuggestedStructure {
  folders: SuggestedFolder[] | null;
  tags: SuggestedStructureTag[] | null;
}

async function generateStudySubject({ input, length }: GenerateStudySubjectParams): Promise<string> {
  const { data, error } = await supabase.functions.invoke("ai-gateway", {
    body: { feature: "note-generate-study-subject", params: { input, length } },
  });

  if (error) {
    throw await handleEdgeFunctionError(error);
  }

  return data?.text || "";
}

async function summarizeNote({ content }: SummarizeNoteParams): Promise<string> {
  const { data, error } = await supabase.functions.invoke("ai-gateway", {
    body: { feature: "note-summarize", params: { content } },
  });

  if (error) {
    throw await handleEdgeFunctionError(error);
  }

  return data?.text || "";
}

async function extractTodos({ content }: ExtractTodosParams): Promise<string> {
  const { data, error } = await supabase.functions.invoke("ai-gateway", {
    body: { feature: "note-extract-todos", params: { content } },
  });

  if (error) {
    throw await handleEdgeFunctionError(error);
  }

  return data?.text || "";
}

async function generateTitle({ content }: GenerateTitleParams): Promise<string> {
  const { data, error } = await supabase.functions.invoke("ai-gateway", {
    body: { feature: "note-generate-title", params: { content } },
  });

  if (error) {
    throw await handleEdgeFunctionError(error);
  }

  return data?.text || "";
}

async function fixGrammarAndSpelling({ content }: FixGrammarAndSpellingParams): Promise<string> {
  const { data, error } = await supabase.functions.invoke("ai-gateway", {
    body: { feature: "note-fix-grammar-and-spelling", params: { content } },
  });

  if (error) {
    throw await handleEdgeFunctionError(error);
  }

  return data?.text || "";
}

async function improveWriting({ content }: ImproveWritingParams): Promise<string> {
  const { data, error } = await supabase.functions.invoke("ai-gateway", {
    body: { feature: "note-improve-writing", params: { content } },
  });

  if (error) {
    throw await handleEdgeFunctionError(error);
  }

  return data?.text || "";
}

async function improveFormatting({ content }: ImproveFormattingParams): Promise<string> {
  const { data, error } = await supabase.functions.invoke("ai-gateway", {
    body: { feature: "note-improve-formatting", params: { content } },
  });

  if (error) {
    throw await handleEdgeFunctionError(error);
  }

  return data?.text || "";
}

async function changeTone({ content, tone }: ChangeToneParams): Promise<string> {
  const { data, error } = await supabase.functions.invoke("ai-gateway", {
    body: { feature: "note-change-tone", params: { content, tone } },
  });

  if (error) {
    throw await handleEdgeFunctionError(error);
  }

  return data?.text || "";
}

async function changeLength({ content, action }: ChangeLengthParams): Promise<string> {
  const { data, error } = await supabase.functions.invoke("ai-gateway", {
    body: { feature: "note-change-length", params: { content, action } },
  });

  if (error) {
    throw await handleEdgeFunctionError(error);
  }

  return data?.text || "";
}

async function translateNote({ content, language }: TranslateNoteParams): Promise<string> {
  const { data, error } = await supabase.functions.invoke("ai-gateway", {
    body: { feature: "note-translate", params: { content, language } },
  });

  if (error) {
    throw await handleEdgeFunctionError(error);
  }

  return data?.text || "";
}

async function askAI({ question, content }: AskAIParams): Promise<string> {
  const { data, error } = await supabase.functions.invoke("ai-gateway", {
    body: { feature: "note-ask-ai", params: { question, content } },
  });

  if (error) {
    throw await handleEdgeFunctionError(error);
  }

  return data?.text || "";
}

async function suggestTags({ content, existingTags }: SuggestTagsParams): Promise<SuggestedTag[]> {
  const { data, error } = await supabase.functions.invoke("ai-gateway", {
    body: { feature: "note-suggest-tags", params: { content, existingTags } },
  });

  if (error) {
    throw await handleEdgeFunctionError(error);
  }

  try {
    const text = data?.text || "[]";
    const parsed = JSON.parse(text);

    // Ensure it's an array and validate/normalize each tag
    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed
      .filter(
        (item: unknown): item is { name: unknown; color: unknown } =>
          typeof item === "object" && item !== null && "name" in item && "color" in item
      )
      .map((tag: { name: unknown; color: unknown }) => ({
        name: typeof tag.name === "string" ? tag.name : "",
        color: normalizeTagColor(tag.color),
      }))
      .filter((tag) => tag.name.length > 0);
  } catch {
    console.error("Failed to parse suggested tags response:", data?.text);
    return [];
  }
}

async function suggestStructure({ useCase, structureType }: SuggestStructureParams): Promise<SuggestedStructure> {
  const { data, error } = await supabase.functions.invoke("ai-gateway", {
    body: { feature: "notes-suggest-structure", params: { useCase, structureType } },
  });

  if (error) {
    throw await handleEdgeFunctionError(error);
  }

  try {
    const text = data?.text || "{}";
    const parsed = JSON.parse(text);

    // Validate the response structure has required properties
    if (typeof parsed !== "object" || parsed === null || !("folders" in parsed) || !("tags" in parsed)) {
      throw new Error("Invalid structure response format: missing folders or tags property");
    }

    // Validate and normalize folders
    const normalizeFolders = (items: unknown[]): SuggestedFolder[] => {
      return items
        .filter(
          (item: unknown): item is { name: unknown; color: unknown; children: unknown } =>
            typeof item === "object" && item !== null && "name" in item && "color" in item && "children" in item
        )
        .map((folder: { name: unknown; color: unknown; children: unknown }) => ({
          name: typeof folder.name === "string" ? folder.name : "",
          color: normalizeFolderColor(folder.color),
          children: Array.isArray(folder.children) ? normalizeFolders(folder.children) : [],
        }))
        .filter((folder) => folder.name.length > 0);
    };

    const folders = Array.isArray(parsed.folders) ? normalizeFolders(parsed.folders) : null;

    // Validate and normalize tags
    const tags = Array.isArray(parsed.tags)
      ? parsed.tags
          .filter(
            (item: unknown): item is { name: unknown; color: unknown } =>
              typeof item === "object" && item !== null && "name" in item && "color" in item
          )
          .map((tag: { name: unknown; color: unknown }) => ({
            name: typeof tag.name === "string" ? tag.name : "",
            color: normalizeTagColor(tag.color),
          }))
          .filter((tag: SuggestedStructureTag) => tag.name.length > 0)
      : null;

    return {
      folders: folders,
      tags: tags,
    };
  } catch (parseError) {
    console.error("Failed to parse suggested structure response:", data?.text, parseError);
    throw new Error("Failed to parse AI response for note structure suggestion");
  }
}

async function speechToText({ transcribedText, language }: SpeechToTextParams): Promise<string> {
  const { data, error } = await supabase.functions.invoke("ai-gateway", {
    body: { feature: "note-speech-to-text", params: { transcribedText, language } },
  });

  if (error) {
    throw await handleEdgeFunctionError(error);
  }

  return data?.text || "";
}

export const aiGatewayFetchers = {
  note: {
    generateStudySubject,
    summarizeNote,
    extractTodos,
    generateTitle,
    fixGrammarAndSpelling,
    improveWriting,
    improveFormatting,
    changeTone,
    changeLength,
    translateNote,
    askAI,
    suggestTags,
    suggestStructure,
    speechToText,
  },
};
